import { Button, Icon, IconButton, IconTile, Popover } from '@humanforce/mango-ui-react';
import { Fragment } from 'react';
import type { PayRunCardDto } from './payRunsApiTypes';
import { PayRunProgressList } from './PayRunProgressList';
import { useExclusiveOverlay, useExclusivePopover } from './usePopoverGroup';
import {
  buildPayRunCardAction,
  buildPayRunCardModel,
  buildPayRunProgressItems,
  formatSystemValue,
  isKnownOperatingState,
  isKnownWorkflowStatus,
} from './payRunsModel';
import './PayRunCard.css';

interface PayRunCardProps {
  payRun: PayRunCardDto;
}

type StatusTone = 'pending' | 'positive' | 'critical' | 'caution' | 'neutral';

const statusIcon = (tone: StatusTone) => ({
  pending: 'status-pending',
  positive: 'status-positive',
  critical: 'status-critical',
  caution: 'status-caution',
  neutral: 'status-no-action',
} as const)[tone];

const CompactTag = ({ icon = false, label, testId, tone }: {
  icon?: boolean | 'chart';
  label: string;
  testId?: string;
  tone: StatusTone;
}) => (
  <span className={`pay-run-card__tag pay-run-card__tag--${tone}`} data-testid={testId}>
    {icon && <Icon name={icon === 'chart' ? 'chart' : statusIcon(tone)} />}
    <span>{label}</span>
  </span>
);

export const statusTone = (value: string): StatusTone => {
  if (value === 'pending' || value === 'open') return 'pending';
  if (value === 'ready_to_pay' || value === 'returned' || value === 'clear' || value === 'complete') return 'positive';
  if (value === 'admitted' || value === 'calculating') return 'pending';
  if (value === 'failed' || value === 'critical') return 'critical';
  if (value === 'not_sent') return 'neutral';
  return 'caution';
};

export const workflowPresentation = (payRun: PayRunCardDto): {
  label: string;
  tone: 'pending' | 'positive' | 'critical' | 'caution' | 'neutral';
} => {
  if (!isKnownWorkflowStatus(payRun.workflowStatus)) return { label: 'Status unavailable', tone: 'neutral' };
  if (payRun.reversedByRunId) return { label: 'Reversed', tone: 'neutral' };
  if (!isKnownOperatingState(payRun.operatingState)) return { label: 'Status unavailable', tone: 'neutral' };
  switch (payRun.workflowStatus) {
    case 'open':
      return { label: 'Pending', tone: 'pending' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'neutral' };
    case 'ready_to_pay':
      return payRun.approval?.isSuperseded
        ? { label: 'Approval superseded', tone: 'neutral' }
        : { label: formatSystemValue(payRun.workflowStatus), tone: statusTone(payRun.workflowStatus) };
    case 'compliance': {
      const compliance = payRun.statusFacets.find((facet) => facet.type === 'compliance');
      if (compliance?.status === 'clear') return { label: 'Compliance', tone: 'positive' };
      if (compliance?.status === 'pending') return { label: 'Compliance', tone: 'pending' };
      return { label: 'Compliance', tone: 'caution' };
    }
    default:
      return { label: formatSystemValue(payRun.workflowStatus), tone: statusTone(payRun.workflowStatus) };
  }
};

export const workflowCardIconPresentation = (payRun: PayRunCardDto): {
  icon: 'clock' | 'status-caution' | 'status-positive' | 'status-no-action';
  color: 'blue' | 'yellow' | 'green' | 'grey';
} => {
  if (!isKnownWorkflowStatus(payRun.workflowStatus) || !isKnownOperatingState(payRun.operatingState) || payRun.reversedByRunId)
    return { icon: 'status-no-action', color: 'grey' };

  switch (payRun.workflowStatus) {
    case 'open':
      return { icon: 'clock', color: 'blue' };
    case 'compliance':
      return { icon: 'status-caution', color: 'yellow' };
    case 'ready_to_pay':
      return { icon: 'status-positive', color: 'green' };
    default:
      return { icon: 'status-no-action', color: 'grey' };
  }
};

export const PayRunCard = ({ payRun }: PayRunCardProps) => {
  const model = buildPayRunCardModel(payRun);
  const workflow = workflowPresentation(payRun);
  const workflowIcon = workflowCardIconPresentation(payRun);
  const calculated = payRun.calculation?.status === 'complete';
  const action = buildPayRunCardAction(payRun);
  const progressPopover = useExclusivePopover(payRun.payId);
  const infoPopover = useExclusiveOverlay(`${payRun.payId}-status-info`);
  const stpFacet = model.facets.find((facet) => facet.type === 'stp');
  const complianceFacet = model.facets.find((facet) => facet.type === 'compliance');
  const blockingCount = complianceFacet?.blockingCount ?? 0;
  const warningCount = complianceFacet?.warningCount ?? 0;
  const stpCritical = stpFacet?.value === 'failed' || stpFacet?.value === 'critical';
  return (
    <article className="pay-run-card" data-testid={`pay-run-card-${payRun.payId}`}>
      <a
        className="pay-run-card__open-overlay"
        href={`/payroll/pay-runs/${encodeURIComponent(payRun.payId)}`}
        aria-label={`Open ${model.title}`}
        data-testid={`pay-run-card-open-${payRun.payId}`}
      />
      <div className="pay-run-card__surface">
        <header className="pay-run-card__header">
          <div className="pay-run-card__heading" data-testid={`pay-run-card-heading-${payRun.payId}`}>
            <IconTile icon={workflowIcon.icon} color={workflowIcon.color} />
            <div className="pay-run-card__heading-copy">
              <h3 className="pay-run-card__title">{model.title}</h3>
              <p className="pay-run-card__period">{model.periodRange}</p>
            </div>
          </div>
          <div
            className="pay-run-card__status"
            data-testid={`pay-run-card-status-${payRun.payId}`}
            onMouseEnter={progressPopover.onHoverEnter}
            onMouseLeave={progressPopover.onHoverLeave}
            onClickCapture={progressPopover.onHoverClickCapture}
          >
            <Popover
              ref={progressPopover.ref}
              onShow={progressPopover.onShow}
              onHide={progressPopover.onHide}
              className="pay-run-progress-popover"
              testId={`pay-run-progress-${payRun.payId}`}
              trigger={(
                <button
                  type="button"
                  className="pay-run-card__progress-trigger"
                  aria-label={`${workflow.label} — show pay run progress`}
                  aria-expanded={progressPopover.isOpen}
                  data-testid={`pay-run-progress-trigger-${payRun.payId}`}
                >
                  <CompactTag label={workflow.label} tone={workflow.tone} />
                </button>
              )}
            >
              <div onMouseEnter={progressPopover.onPopoverEnter} onMouseLeave={progressPopover.onHoverLeave}>
                <PayRunProgressList
                  testId={`pay-run-progress-list-${payRun.payId}`}
                  label="Progress"
                  idPrefix={`pay-run-progress-${payRun.payId}`}
                  items={buildPayRunProgressItems(payRun.workflowStatus, payRun.payId, payRun.approval, payRun.operatingState)}
                />
              </div>
            </Popover>
          </div>
        </header>

      <div className="pay-run-card__statuses" aria-label="Pay Run statuses">
        <div
          className="pay-run-card__status-info"
          data-testid={`pay-run-card-status-info-${payRun.payId}`}
          onMouseEnter={infoPopover.showByHover}
          onMouseLeave={infoPopover.scheduleHide}
        >
          <Button
            variant="tertiary"
            size="small"
            className="pay-run-card__status-info-trigger"
            testId={`pay-run-card-status-info-button-${payRun.payId}`}
            onClick={infoPopover.toggle}
          >
            <span className="pay-run-card__status-info-trigger-content">
              <Icon name="info" />
              <span className="sr-only">View STP and Compliance status</span>
            </span>
          </Button>
        </div>
        {model.facets.map((facet, index) => (
          <Fragment key={`${facet.type}-${facet.value}`}>
            <div className="pay-run-card__status-facet">
              <span className="mgo-text-s mgo-weight-strong">
                {facet.type === 'stp' ? 'STP' : formatSystemValue(facet.type)}
              </span>
              {facet.type === 'compliance' && (facet.blockingCount > 0 || facet.warningCount > 0) ? (
                <div className="mgo-cluster mgo-gap-xs">
                  {facet.blockingCount > 0 && (
                    <CompactTag icon label={`${facet.blockingCount}`} tone="critical"
                      testId={`pay-run-card-compliance-blocking-${payRun.payId}`} />
                  )}
                  {facet.warningCount > 0 && (
                    <CompactTag icon label={`${facet.warningCount}`} tone="caution"
                      testId={`pay-run-card-compliance-warning-${payRun.payId}`} />
                  )}
                </div>
              ) : (
                <CompactTag icon label={facet.value === 'not_sent' ? 'Not sent' : facet.label.split(': ')[1]}
                  tone={statusTone(facet.value)} />
              )}
            </div>
            {index < model.facets.length - 1 && (
              <span
                aria-hidden="true"
                className="pay-run-card__status-divider"
                data-testid={`pay-run-card-status-divider-${payRun.payId}`}
              />
            )}
          </Fragment>
        ))}
      </div>

      <div className="pay-run-card__calculations" aria-label="Pay Run calculations">
        {(() => {
            const rows = [
              ['Super', calculated ? model.super : '—'],
              ['Gross Pay', calculated ? model.gross : '—'],
              ['Tax', calculated ? model.tax : '—'],
              ['Net Pay', calculated ? model.netPay : '—'],
            ];
            const netPayIndex = rows.length - 1;
            return rows.map(([label, value], index) => (
              <div
                key={label}
                className={`mgo-split mgo-gap-l pay-run-card__calculation-row${index === netPayIndex ? ' pay-run-card__calculation-row--net' : ''}`}
              >
                <span className={`mgo-text-s ${index === netPayIndex ? '' : 'mgo-color-muted'}`}>{label}</span>
                <span className="mgo-text-s mgo-weight-strong">{value}</span>
              </div>
            ));
        })()}
      </div>

      <div className="mgo-cluster mgo-gap-s pay-run-card__metadata" data-testid={`pay-run-card-footer-details-${payRun.payId}`}>
        <span className="mgo-text-xs mgo-weight-strong">Payment Date: {model.paymentDate}</span>
        {model.operatingStateValue === 'trial_pay' && <CompactTag label={model.operatingStateLabel} tone="neutral" />}
        <CompactTag label={model.runTypeLabel} tone="neutral" />
        <CompactTag label={model.frequencyLabel} tone="neutral" />
      </div>

      {action && (
        <div className="pay-run-card__action">
          {action.icon ? (
            <Button href={action.href} variant={action.variant} fullWidth testId={`pay-run-action-${payRun.payId}`}>
              <span className="pay-run-card__action-content" data-testid={`pay-run-action-content-${payRun.payId}`}>
                <span aria-hidden="true"><Icon name={action.icon} /></span>
                <span>{action.label}</span>
              </span>
            </Button>
          ) : (
            <Button label={action.label} href={action.href} variant={action.variant}
              fullWidth testId={`pay-run-action-${payRun.payId}`} />
          )}
        </div>
      )}
      </div>
      {infoPopover.isOpen && (
        <div
          aria-label={`${model.title} status review`}
          className="pay-run-status-popover"
          data-testid={`pay-run-status-popover-${payRun.payId}`}
          role="dialog"
          onMouseEnter={infoPopover.cancelScheduledHide}
          onMouseLeave={infoPopover.scheduleHide}
        >
          <div className="pay-run-status-popover__content">
            <div className="pay-run-status-popover__header">
              <span className="mgo-text-m mgo-weight-strong">{model.title}</span>
              <div className="pay-run-status-popover__close">
                <IconButton
                  icon="close"
                  label="Close status review"
                  variant="tertiary"
                  size="small"
                  onClick={infoPopover.hide}
                />
              </div>
            </div>

            <div className="mgo-cluster mgo-gap-xs pay-run-status-popover__summary" aria-label="Status summary">
              {stpFacet && <CompactTag icon label={`STP ${formatSystemValue(stpFacet.value)}`} tone={statusTone(stpFacet.value)} />}
              {blockingCount > 0 && <CompactTag icon label={`${blockingCount}`} tone="critical" />}
              {warningCount > 0 && <CompactTag icon label={`${warningCount}`} tone="caution" />}
            </div>

            {stpFacet && (
              <section className={`pay-run-status-popover__issue pay-run-status-popover__issue--${stpCritical ? 'critical' : 'neutral'}`}>
                <div className={`mgo-cluster mgo-gap-s pay-run-status-popover__issue-stat ${stpCritical ? 'mgo-color-critical' : 'mgo-color-muted'}`}>
                  <Icon name={stpCritical ? 'status-critical' : 'status-no-action'} width="fixed" />
                  <span className="mgo-text-xs mgo-weight-strong">STP {formatSystemValue(stpFacet.value).toUpperCase()}</span>
                </div>
                <span className="mgo-text-s mgo-weight-strong">{stpFacet.detail || 'STP submission requires review'}</span>
                <span className="mgo-text-xs mgo-color-muted">Open the Pay Run to review the submission details.</span>
              </section>
            )}

            {blockingCount > 0 && (
              <section className="pay-run-status-popover__issue pay-run-status-popover__issue--neutral">
                <div className="mgo-cluster mgo-gap-s pay-run-status-popover__issue-stat mgo-color-critical">
                  <Icon name="status-critical" width="fixed" />
                  <span className="mgo-text-xs mgo-weight-strong">{blockingCount} ERROR{blockingCount === 1 ? '' : 'S'}</span>
                </div>
                <span className="mgo-text-s mgo-weight-strong">Compliance review required</span>
                <span className="mgo-text-xs mgo-color-muted">{complianceFacet?.detail || 'Resolve blocking compliance issues before progressing.'}</span>
              </section>
            )}

            {warningCount > 0 && (
              <section className="pay-run-status-popover__issue pay-run-status-popover__issue--neutral">
                <div className="mgo-cluster mgo-gap-s pay-run-status-popover__issue-stat mgo-color-caution">
                  <Icon name="status-caution" width="fixed" />
                  <span className="mgo-text-xs mgo-weight-strong">{warningCount} WARNING{warningCount === 1 ? '' : 'S'}</span>
                </div>
                <span className="mgo-text-s mgo-weight-strong">Compliance warning</span>
                <span className="mgo-text-xs mgo-color-muted">Review the affected employees and calculation inputs.</span>
              </section>
            )}

            <Button label="Review all" variant="primary" fullWidth href={`/payroll/pay-runs/${encodeURIComponent(payRun.payId)}`} />
          </div>
        </div>
      )}
    </article>
  );
};
